/**
 * Reusable FBO wrapper for WebGL 2.0.
 * Supports RGBA16F (HDR) and RGBA8 (LDR) color attachments.
 * Optional depth renderbuffer for scene rendering.
 */
export class FrameBuffer {
  private _gl: WebGL2RenderingContext;
  private _fbo: WebGLFramebuffer;
  private _texture: WebGLTexture;
  private _depthRb: WebGLRenderbuffer | null = null;
  private _width: number;
  private _height: number;
  private _internalFormat: number;
  private _filter: number;
  private _withDepth: boolean;

  constructor(
    gl: WebGL2RenderingContext,
    width: number,
    height: number,
    internalFormat: number,
    filter: number,
    withDepth: boolean,
  ) {
    this._gl = gl;
    this._width = width;
    this._height = height;
    this._internalFormat = internalFormat;
    this._filter = filter;
    this._withDepth = withDepth;

    this._fbo = gl.createFramebuffer()!;
    this._texture = gl.createTexture()!;
    this._createAttachments();
  }

  get texture(): WebGLTexture { return this._texture; }
  get width(): number { return this._width; }
  get height(): number { return this._height; }

  bind(): void {
    this._gl.bindFramebuffer(this._gl.FRAMEBUFFER, this._fbo);
    this._gl.viewport(0, 0, this._width, this._height);
  }

  unbind(): void {
    this._gl.bindFramebuffer(this._gl.FRAMEBUFFER, null);
  }

  resize(width: number, height: number): void {
    if (width === this._width && height === this._height) return;
    this._width = width;
    this._height = height;
    this._deleteAttachments();
    this._createAttachments();
  }

  dispose(): void {
    this._deleteAttachments();
    this._gl.deleteFramebuffer(this._fbo);
  }

  private _createAttachments(): void {
    const gl = this._gl;
    const isFloat = this._internalFormat === gl.RGBA16F;
    const format = gl.RGBA;
    const type = isFloat ? gl.HALF_FLOAT : gl.UNSIGNED_BYTE;

    gl.bindTexture(gl.TEXTURE_2D, this._texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, this._internalFormat,
      this._width, this._height, 0, format, type, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, this._filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, this._filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    gl.bindFramebuffer(gl.FRAMEBUFFER, this._fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D, this._texture, 0);

    if (this._withDepth) {
      this._depthRb = gl.createRenderbuffer()!;
      gl.bindRenderbuffer(gl.RENDERBUFFER, this._depthRb);
      gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24,
        this._width, this._height);
      gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT,
        gl.RENDERBUFFER, this._depthRb);
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.bindTexture(gl.TEXTURE_2D, null);
  }

  private _deleteAttachments(): void {
    const gl = this._gl;
    gl.deleteTexture(this._texture);
    this._texture = gl.createTexture()!;
    if (this._depthRb) {
      gl.deleteRenderbuffer(this._depthRb);
      this._depthRb = null;
    }
  }
}
